import type {
  AdminAuthRealmConfig,
  ApiBackedModuleId,
  AuthRealmsConfig,
  ClmEnvironment,
  RegistrationPaymentsConfig,
  RuntimeConfig,
  RuntimeFeatureFlagKey,
} from "@/types/clm-web";
import {
  API_BACKED_MODULE_IDS,
  AUTH_REALM_IDS,
  CLM_ENVIRONMENTS,
  RUNTIME_FEATURE_FLAG_KEYS,
} from "@/types/clm-web";

const RUNTIME_CONFIG_GLOBAL = "__CLM_WEB_RUNTIME_CONFIG__";
const SECRET_KEY_PATTERN =
  /(secret|password|private[_-]?key|credential|token|client[_-]?secret|aws[_-]?access|aws[_-]?secret)/i;
const PLACEHOLDER_BUILD_SHA = "local-dev";
const PLACEHOLDER_BUILD_TIME = "1970-01-01T00:00:00.000Z";
const RUNTIME_CONFIG_TOP_LEVEL_KEYS = [
  "environment",
  "appBaseUrl",
  "authBaseUrl",
  "authProviderTopology",
  "cognitoIssuer",
  "cognitoJwksEndpoint",
  "cognitoClientId",
  "authCallbackUrl",
  "authLogoutUrl",
  "legacyAuthRedirectAllowed",
  "trackerBaseUrl",
  "trackerSsoSource",
  "wikiBaseUrl",
  "moodleBaseUrl",
  "authRealms",
  "apiBaseUrls",
  "buildSha",
  "buildTime",
  "featureFlags",
  "registrationPayments",
] as const;
const ADMIN_AUTH_REALM_KEYS = [
  "spaOrigin",
  "authBaseUrl",
  "cognitoIssuer",
  "cognitoJwksEndpoint",
  "cognitoClientId",
  "authCallbackUrl",
  "authLogoutUrl",
  "identityProvider",
  "samlProviderName",
  "authorizationClaims",
] as const;
const ADMIN_AUTHORIZATION_CLAIM_KEYS = ["appRoleClaimName", "requiredAppRoleValue"] as const;
const REGISTRATION_PAYMENTS_KEYS = [
  "stripePublishableKey",
  "statusPollIntervalSeconds",
  "statusPollWindowSeconds",
] as const;
const ADMIN_SPA_HOST_PREFIX = "admin.";
const ADMIN_AUTH_HOST_PREFIX = "admin-auth.";
// Moodle hosts per stage (004-6 contract § 2): prod is the bare courses host,
// unlike the wiki/tracker `-prod` pattern.
const MOODLE_HOSTS: Record<ClmEnvironment, string> = {
  local: "courses-sandbox.theclm.org",
  sandbox: "courses-sandbox.theclm.org",
  test: "courses-test.theclm.org",
  prod: "courses.theclm.org",
};
const COGNITO_ISSUER_HOST_PATTERN = /^cognito-idp\.[a-z0-9-]+\.amazonaws\.com$/;
const SAFE_LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

export type RuntimeConfigSource = "global" | "fetch" | "inline-fallback";

export interface RuntimeConfigLoadOptions {
  environment?: ClmEnvironment;
  configUrl?: string;
  fetcher?: typeof fetch;
  allowInlineFallback?: boolean;
}

export interface RuntimeConfigLoadResult {
  config: RuntimeConfig;
  source: RuntimeConfigSource;
}

declare global {
  interface Window {
    [RUNTIME_CONFIG_GLOBAL]?: unknown;
  }
}

let activeRuntimeConfig: RuntimeConfig | undefined;

export function getRuntimeConfigEnvironment(): ClmEnvironment {
  const configured = import.meta.env.VITE_CLM_ENVIRONMENT as string | undefined;

  if (configured && isClmEnvironment(configured)) {
    return configured;
  }

  return import.meta.env.MODE === "production" ? "prod" : "local";
}

export function shouldAllowInlineRuntimeFallback(
  environment = getRuntimeConfigEnvironment(),
): boolean {
  if (environment === "local") {
    return true;
  }

  if (import.meta.env.MODE === "test") {
    return environment === "test";
  }

  const hostname = typeof window !== "undefined" ? window.location.hostname : undefined;
  return environment === "test" && Boolean(hostname && SAFE_LOCAL_HOSTNAMES.has(hostname));
}

export async function loadRuntimeConfig(
  options: RuntimeConfigLoadOptions = {},
): Promise<RuntimeConfigLoadResult> {
  if (typeof window !== "undefined" && window[RUNTIME_CONFIG_GLOBAL]) {
    const config = parseRuntimeConfig(window[RUNTIME_CONFIG_GLOBAL]);
    activeRuntimeConfig = config;
    return { config, source: "global" };
  }

  const environment = options.environment ?? getRuntimeConfigEnvironment();
  const configUrl = options.configUrl ?? `/config/${environment}.json`;
  const fetcher = options.fetcher ?? globalThis.fetch;

  if (fetcher) {
    const response = await fetcher(configUrl, { credentials: "same-origin" });

    if (response.ok) {
      const config = parseRuntimeConfig(await response.json());
      activeRuntimeConfig = config;
      return { config, source: "fetch" };
    }

    if (!options.allowInlineFallback) {
      throw new Error(`Unable to load runtime config from ${configUrl}: ${response.status}`);
    }
  }

  if (options.allowInlineFallback) {
    const config = createLocalRuntimeConfig();
    activeRuntimeConfig = config;
    return { config, source: "inline-fallback" };
  }

  throw new Error(`Unable to load runtime config from ${configUrl}`);
}

export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  if (!isRecord(value)) {
    throw new Error("Runtime config must be an object.");
  }

  assertNoSecretLikeKeys(value);
  assertNoUnknownKeys(value, RUNTIME_CONFIG_TOP_LEVEL_KEYS, "Runtime config");

  const environment = parseEnvironment(value.environment);
  const appBaseUrl = parseUrl(value.appBaseUrl, "appBaseUrl");
  const authBaseUrl = parseUrl(value.authBaseUrl, "authBaseUrl");
  const authProviderTopology = parseOptionalAuthProviderTopology(value.authProviderTopology);
  const cognitoIssuer = parseOptionalUrl(value.cognitoIssuer, "cognitoIssuer");
  const cognitoJwksEndpoint = parseOptionalUrl(value.cognitoJwksEndpoint, "cognitoJwksEndpoint");
  const cognitoClientId = parseOptionalString(value.cognitoClientId, "cognitoClientId");
  const authCallbackUrl = parseOptionalUrl(value.authCallbackUrl, "authCallbackUrl");
  const authLogoutUrl = parseOptionalUrl(value.authLogoutUrl, "authLogoutUrl");
  const legacyAuthRedirectAllowed = parseOptionalBoolean(
    value.legacyAuthRedirectAllowed,
    "legacyAuthRedirectAllowed",
  );
  const trackerBaseUrl = parseOptionalUrl(value.trackerBaseUrl, "trackerBaseUrl");
  const trackerSsoSource = parseOptionalTrackerSsoSource(value.trackerSsoSource);
  const wikiBaseUrl = parseWikiBaseUrl(value.wikiBaseUrl, environment);
  const moodleBaseUrl = parseMoodleBaseUrl(value.moodleBaseUrl, environment);
  const authRealms = parseAuthRealms(value.authRealms);

  if (environment === "local" && authRealms) {
    throw new Error("Runtime config must not define authRealms in the local environment.");
  }

  const apiBaseUrls = parseApiBaseUrls(value.apiBaseUrls);
  const buildSha = parseRequiredString(value.buildSha, "buildSha");
  const buildTime = parseIsoDateTime(value.buildTime, "buildTime");
  const featureFlags = parseFeatureFlags(value.featureFlags);
  const registrationPayments = parseRegistrationPayments(value.registrationPayments);

  if (environment !== "local") {
    assertRequiredAuthTopology({
      authProviderTopology,
      cognitoIssuer,
      cognitoJwksEndpoint,
      cognitoClientId,
      authCallbackUrl,
      authLogoutUrl,
      legacyAuthRedirectAllowed,
      trackerBaseUrl,
      trackerSsoSource,
    });
  }

  return {
    environment,
    appBaseUrl,
    authBaseUrl,
    ...(authProviderTopology ? { authProviderTopology } : {}),
    ...(cognitoIssuer ? { cognitoIssuer } : {}),
    ...(cognitoJwksEndpoint ? { cognitoJwksEndpoint } : {}),
    ...(cognitoClientId ? { cognitoClientId } : {}),
    ...(authCallbackUrl ? { authCallbackUrl } : {}),
    ...(authLogoutUrl ? { authLogoutUrl } : {}),
    ...(legacyAuthRedirectAllowed !== undefined ? { legacyAuthRedirectAllowed } : {}),
    ...(trackerBaseUrl ? { trackerBaseUrl } : {}),
    ...(trackerSsoSource ? { trackerSsoSource } : {}),
    wikiBaseUrl,
    moodleBaseUrl,
    ...(authRealms ? { authRealms } : {}),
    apiBaseUrls,
    buildSha,
    buildTime,
    ...(featureFlags ? { featureFlags } : {}),
    ...(registrationPayments ? { registrationPayments } : {}),
  };
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!activeRuntimeConfig) {
    throw new Error(
      "Runtime config has not been loaded. Call loadRuntimeConfig() during app bootstrap.",
    );
  }

  return activeRuntimeConfig;
}

export function setRuntimeConfigForTests(config: RuntimeConfig | undefined): void {
  activeRuntimeConfig = config ? parseRuntimeConfig(config) : undefined;
}

export function createLocalRuntimeConfig(): RuntimeConfig {
  return {
    environment: "local",
    appBaseUrl: "http://localhost:5173",
    authBaseUrl: "http://localhost:8787",
    wikiBaseUrl: "https://wiki-sandbox.theclm.org",
    moodleBaseUrl: "https://courses-sandbox.theclm.org",
    apiBaseUrls: {
      ucc: "http://localhost:8788",
      events: "http://localhost:8789",
      courses: "http://localhost:8790",
      compliance: "http://localhost:8791",
      financial: "http://localhost:8792",
      documents: "http://localhost:8793",
      search: "http://localhost:8794",
      cms: "http://localhost:8795",
    },
    buildSha: PLACEHOLDER_BUILD_SHA,
    buildTime: PLACEHOLDER_BUILD_TIME,
    featureFlags: {},
  };
}

function parseEnvironment(value: unknown): ClmEnvironment {
  if (typeof value === "string" && isClmEnvironment(value)) {
    return value;
  }

  throw new Error(`Runtime config environment must be one of: ${CLM_ENVIRONMENTS.join(", ")}.`);
}

function isClmEnvironment(value: string): value is ClmEnvironment {
  return CLM_ENVIRONMENTS.includes(value as ClmEnvironment);
}

function parseUrl(value: unknown, fieldName: string): string {
  const urlText = parseRequiredString(value, fieldName);

  try {
    const url = new URL(urlText);
    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error("invalid protocol");
    }
    return url.toString().replace(/\/$/, "");
  } catch {
    throw new Error(`Runtime config ${fieldName} must be an absolute HTTP(S) URL.`);
  }
}

function parseOptionalUrl(value: unknown, fieldName: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  return parseUrl(value, fieldName);
}

function parseOptionalString(value: unknown, fieldName: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  return parseRequiredString(value, fieldName);
}

function parseOptionalBoolean(value: unknown, fieldName: string): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  throw new Error(`Runtime config ${fieldName} must be boolean.`);
}

function parseOptionalAuthProviderTopology(
  value: unknown,
): RuntimeConfig["authProviderTopology"] | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === "cognito-auth-service-bff") {
    return value;
  }

  throw new Error("Runtime config authProviderTopology must be cognito-auth-service-bff.");
}

function parseOptionalTrackerSsoSource(value: unknown): RuntimeConfig["trackerSsoSource"] {
  if (value === undefined) {
    return undefined;
  }

  if (value === "clm-core") {
    return value;
  }

  throw new Error("Runtime config trackerSsoSource must be clm-core for MVP.");
}

function parseWikiBaseUrl(value: unknown, environment: ClmEnvironment): string {
  const rawWikiBaseUrl =
    value === undefined
      ? `https://wiki-${environment === "local" ? "sandbox" : environment}.theclm.org`
      : value;

  return parseUrl(rawWikiBaseUrl, "wikiBaseUrl");
}

function parseMoodleBaseUrl(value: unknown, environment: ClmEnvironment): string {
  const rawMoodleBaseUrl = value === undefined ? `https://${MOODLE_HOSTS[environment]}` : value;
  const parsed = parseHttpsUrl(rawMoodleBaseUrl, "moodleBaseUrl");

  // The launch path is appended verbatim, so only a bare origin is safe.
  if (parsed !== new URL(parsed).origin) {
    throw new Error("Runtime config moodleBaseUrl must be a bare HTTPS origin.");
  }

  return parsed;
}

function assertRequiredAuthTopology(
  values: Pick<
    RuntimeConfig,
    | "authProviderTopology"
    | "cognitoIssuer"
    | "cognitoJwksEndpoint"
    | "cognitoClientId"
    | "authCallbackUrl"
    | "authLogoutUrl"
    | "legacyAuthRedirectAllowed"
    | "trackerBaseUrl"
    | "trackerSsoSource"
  >,
): void {
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined) {
      throw new Error(`Runtime config ${key} is required for deployed environments.`);
    }
  }
}

function parseAuthRealms(value: unknown): AuthRealmsConfig | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!isRecord(value)) {
    throw new Error("Runtime config authRealms must be an object when provided.");
  }

  assertNoUnknownKeys(value, AUTH_REALM_IDS, "Runtime config authRealms");

  const admin =
    value.admin === undefined ? undefined : parseAdminAuthRealm(value.admin, "authRealms.admin");

  return admin ? { admin } : {};
}

function parseAdminAuthRealm(value: unknown, scope: string): AdminAuthRealmConfig {
  if (!isRecord(value)) {
    throw new Error(`Runtime config ${scope} must be an object.`);
  }

  assertNoUnknownKeys(value, ADMIN_AUTH_REALM_KEYS, `Runtime config ${scope}`);

  const spaOrigin = parseHttpsUrl(value.spaOrigin, `${scope}.spaOrigin`);
  const spaOriginUrl = new URL(spaOrigin);
  if (!spaOriginUrl.hostname.startsWith(ADMIN_SPA_HOST_PREFIX)) {
    throw new Error(
      `Runtime config ${scope}.spaOrigin host must start with ${ADMIN_SPA_HOST_PREFIX}`,
    );
  }
  if (spaOrigin !== spaOriginUrl.origin) {
    throw new Error(`Runtime config ${scope}.spaOrigin must be an origin without a path.`);
  }

  const authBaseUrl = parseHttpsUrl(value.authBaseUrl, `${scope}.authBaseUrl`);
  const authBaseUrlHost = new URL(authBaseUrl).hostname;
  if (!authBaseUrlHost.startsWith(ADMIN_AUTH_HOST_PREFIX)) {
    throw new Error(
      `Runtime config ${scope}.authBaseUrl host must start with ${ADMIN_AUTH_HOST_PREFIX}`,
    );
  }

  const cognitoIssuer = parseHttpsUrl(value.cognitoIssuer, `${scope}.cognitoIssuer`);
  const issuerUrl = new URL(cognitoIssuer);
  const issuerPathSegments = issuerUrl.pathname.split("/").filter(Boolean);
  if (!COGNITO_ISSUER_HOST_PATTERN.test(issuerUrl.hostname) || issuerPathSegments.length !== 1) {
    throw new Error(
      `Runtime config ${scope}.cognitoIssuer must be a Cognito user-pool issuer URL.`,
    );
  }

  const cognitoJwksEndpoint = parseHttpsUrl(
    value.cognitoJwksEndpoint,
    `${scope}.cognitoJwksEndpoint`,
  );
  if (cognitoJwksEndpoint !== `${cognitoIssuer}/.well-known/jwks.json`) {
    throw new Error(
      `Runtime config ${scope}.cognitoJwksEndpoint must be the issuer /.well-known/jwks.json endpoint.`,
    );
  }

  const cognitoClientId = parseRequiredString(value.cognitoClientId, `${scope}.cognitoClientId`);

  const authCallbackUrl = parseHttpsUrl(value.authCallbackUrl, `${scope}.authCallbackUrl`);
  if (new URL(authCallbackUrl).origin !== new URL(authBaseUrl).origin) {
    throw new Error(`Runtime config ${scope}.authCallbackUrl must be under authBaseUrl.`);
  }

  const authLogoutUrl = parseHttpsUrl(value.authLogoutUrl, `${scope}.authLogoutUrl`);
  if (new URL(authLogoutUrl).origin !== spaOriginUrl.origin) {
    throw new Error(`Runtime config ${scope}.authLogoutUrl must be under spaOrigin.`);
  }

  if (value.identityProvider !== "entra-saml") {
    throw new Error(`Runtime config ${scope}.identityProvider must be entra-saml.`);
  }

  const samlProviderName = parseRequiredString(value.samlProviderName, `${scope}.samlProviderName`);

  if (!isRecord(value.authorizationClaims)) {
    throw new Error(`Runtime config ${scope}.authorizationClaims must be an object.`);
  }
  assertNoUnknownKeys(
    value.authorizationClaims,
    ADMIN_AUTHORIZATION_CLAIM_KEYS,
    `Runtime config ${scope}.authorizationClaims`,
  );
  const authorizationClaims = {
    appRoleClaimName: parseRequiredString(
      value.authorizationClaims.appRoleClaimName,
      `${scope}.authorizationClaims.appRoleClaimName`,
    ),
    requiredAppRoleValue: parseRequiredString(
      value.authorizationClaims.requiredAppRoleValue,
      `${scope}.authorizationClaims.requiredAppRoleValue`,
    ),
  };

  return {
    spaOrigin,
    authBaseUrl,
    cognitoIssuer,
    cognitoJwksEndpoint,
    cognitoClientId,
    authCallbackUrl,
    authLogoutUrl,
    identityProvider: "entra-saml",
    samlProviderName,
    authorizationClaims,
  };
}

function parseHttpsUrl(value: unknown, fieldName: string): string {
  const parsed = parseUrl(value, fieldName);

  if (!parsed.startsWith("https://")) {
    throw new Error(`Runtime config ${fieldName} must be an absolute HTTPS URL.`);
  }

  return parsed;
}

function parseApiBaseUrls(value: unknown): Record<ApiBackedModuleId, string> {
  if (!isRecord(value)) {
    throw new Error("Runtime config apiBaseUrls must be an object.");
  }

  assertNoUnknownKeys(value, API_BACKED_MODULE_IDS, "Runtime config apiBaseUrls");

  const parsed = {} as Record<ApiBackedModuleId, string>;

  for (const moduleId of API_BACKED_MODULE_IDS) {
    if (!Object.hasOwn(value, moduleId)) {
      throw new Error(`Runtime config apiBaseUrls.${moduleId} is required.`);
    }

    parsed[moduleId] = parseUrl(value[moduleId], `apiBaseUrls.${moduleId}`);
  }

  return parsed;
}

function parseFeatureFlags(
  value: unknown,
): Partial<Record<RuntimeFeatureFlagKey, boolean>> | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!isRecord(value)) {
    throw new Error("Runtime config featureFlags must be an object when provided.");
  }

  assertNoUnknownKeys(value, RUNTIME_FEATURE_FLAG_KEYS, "Runtime config featureFlags");

  const flags: Partial<Record<RuntimeFeatureFlagKey, boolean>> = {};
  for (const key of RUNTIME_FEATURE_FLAG_KEYS) {
    const enabled = Object.hasOwn(value, key) ? value[key] : undefined;
    if (enabled === undefined) {
      continue;
    }

    if (typeof enabled !== "boolean") {
      throw new Error(`Runtime config featureFlags.${key} must be boolean.`);
    }

    flags[key] = enabled;
  }

  return flags;
}

function parseRegistrationPayments(value: unknown): RegistrationPaymentsConfig | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (!isRecord(value)) {
    throw new Error("Runtime config registrationPayments must be an object when provided.");
  }

  assertNoUnknownKeys(value, REGISTRATION_PAYMENTS_KEYS, "Runtime config registrationPayments");

  const parsed: RegistrationPaymentsConfig = {};

  if (value.stripePublishableKey !== undefined) {
    // Normalized (trimmed) once here — every consumer reads the canonical key.
    const key = parseRequiredString(
      value.stripePublishableKey,
      "registrationPayments.stripePublishableKey",
    ).trim();
    // Allow only public publishable (pk_) keys; secret/restricted keys and
    // any other non-publishable value fail closed (mirrors the generator).
    if (!key.startsWith("pk_")) {
      throw new Error(
        "Runtime config registrationPayments.stripePublishableKey must be a publishable (pk_) key.",
      );
    }
    parsed.stripePublishableKey = key;
  }

  for (const field of ["statusPollIntervalSeconds", "statusPollWindowSeconds"] as const) {
    const seconds = value[field];
    if (seconds === undefined) {
      continue;
    }
    const { min, max } = STATUS_POLL_BOUNDS_SECONDS[field];
    if (
      typeof seconds !== "number" ||
      !Number.isFinite(seconds) ||
      seconds < min ||
      seconds > max
    ) {
      throw new Error(
        `Runtime config registrationPayments.${field} must be a number between ${min} and ${max} seconds.`,
      );
    }
    parsed[field] = seconds;
  }

  return parsed;
}

/**
 * Operational guardrails around the contract defaults (5s interval / 120s
 * window; 006-6 registration-checkout-ui-contract). Out-of-bounds values are
 * rejected, never clamped. Keep textually in sync with the deploy-time
 * generator in scripts/write-runtime-config.mjs (no shared module spans
 * scripts/ and src/).
 */
const STATUS_POLL_BOUNDS_SECONDS = {
  statusPollIntervalSeconds: { min: 1, max: 60 },
  statusPollWindowSeconds: { min: 10, max: 900 },
} as const;

function parseRequiredString(value: unknown, fieldName: string): string {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  throw new Error(`Runtime config ${fieldName} must be a non-empty string.`);
}

function parseIsoDateTime(value: unknown, fieldName: string): string {
  const text = parseRequiredString(value, fieldName);
  const timestamp = Date.parse(text);

  if (Number.isNaN(timestamp)) {
    throw new Error(`Runtime config ${fieldName} must be an ISO date-time string.`);
  }

  return new Date(timestamp).toISOString();
}

function assertNoSecretLikeKeys(value: unknown, path: string[] = []): void {
  if (!isRecord(value)) {
    return;
  }

  for (const [key, nested] of Object.entries(value)) {
    const fullPath = [...path, key].join(".");
    if (SECRET_KEY_PATTERN.test(key)) {
      throw new Error(`Runtime config contains forbidden secret-like key: ${fullPath}`);
    }
    assertNoSecretLikeKeys(nested, [...path, key]);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assertNoUnknownKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  scope: string,
): void {
  const allowed = new Set(allowedKeys);

  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      throw new Error(`${scope} contains unknown key: ${key}`);
    }
  }
}
